import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminPage } from "@/components/admin/AdminPage";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { ProviderCard } from "@/components/backup/ProviderCard";
import { ScheduleCard } from "@/components/backup/ScheduleCard";
import { StatusCard } from "@/components/backup/StatusCard";
import { AuditLog } from "@/components/backup/AuditLog";
import { FolderMapping } from "@/components/backup/FolderMapping";
import { PlanPreview } from "@/components/backup/PlanPreview";
import { Card } from "@/components/ui/card";
import { HardDriveDownload, Cloud, Check } from "lucide-react";

const MODES = [
  { key: "manual", label: "Manual Project Archive", icon: HardDriveDownload, note: "Download each project as a structured ZIP for your company server." },
  { key: "onedrive", label: "OneDrive", icon: Cloud, note: "Automatically mirror project evidence to Microsoft OneDrive." },
  { key: "sharepoint", label: "SharePoint", icon: Cloud, note: "Automatically mirror project evidence to a SharePoint library." },
];

function StorageModeCard({ mode, onChange }) {
  return (
    <Card className="p-5 border-slate-200" data-testid="storage-mode-card">
      <p className="overline">Storage Mode</p>
      <p className="mt-1 text-sm text-slate-500">Choose how project documents are stored for this workspace.</p>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {MODES.map((m) => {
          const active = mode === m.key;
          const Icon = m.icon;
          return (
            <button key={m.key} type="button" onClick={() => onChange(m.key)} data-testid={`storage-mode-${m.key}`}
              className={`text-left rounded-lg border p-3 transition ${active ? "border-[#2563EB] ring-1 ring-[#2563EB] bg-blue-50/40" : "border-slate-200 hover:border-slate-400"}`}>
              <div className="flex items-center justify-between">
                <Icon className={`h-5 w-5 ${active ? "text-[#2563EB]" : "text-slate-500"}`} />
                {active && <Check className="h-4 w-4 text-[#2563EB]" />}
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-900">{m.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{m.note}</div>
            </button>
          );
        })}
      </div>
      {mode === "manual" && (
        <div className="mt-4 rounded-md border border-blue-100 bg-blue-50/50 p-3 text-sm text-slate-700" data-testid="manual-mode-note">
          Project documents can be downloaded as a structured ZIP archive for storage on your company server or preferred
          storage system. Use <strong>Download Project Archive</strong> from any Project Detail or the ERP Handover panel. Cloud
          providers below remain available for future use.
        </div>
      )}
    </Card>
  );
}

export default function AdminBackupPage() {
  const [cfg, setCfg] = useState(null);
  const [providers, setProviders] = useState([]);
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [running, setRunning] = useState(false);
  const [projects, setProjects] = useState([]);
  const [planFor, setPlanFor] = useState("");
  const [plan, setPlan] = useState(null);
  const [params, setParams] = useSearchParams();

  const refresh = useCallback(() => {
    api.get("/admin/backup").then((r) => { setCfg(r.data); setDirty(false); }).catch(() => {});
    api.get("/admin/backup/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/admin/backup/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    api.get("/admin/backup/providers").then((r) => { const list = r.data.providers; list.redirect_uri = r.data.redirect_uri; setProviders(list); });
    api.get("/projects").then((r) => setProjects(r.data));
  }, [refresh]);
  useEffect(() => {
    if (params.get("connected")) { toast.success(`${params.get("connected")} account connected`); setParams({}); }
    if (params.get("error")) { toast.error(`Connection failed: ${params.get("error")}`); setParams({}); }
  }, [params, setParams]);
  useEffect(() => { if (planFor) api.get(`/projects/${planFor}/backup-plan`).then((r) => setPlan(r.data)); }, [planFor]);

  const set = (patch) => { setCfg((c) => ({ ...c, ...patch })); setDirty(true); };
  const setConn = (k, v) => set({ connection: { ...cfg.connection, [k]: v } });
  const setFolder = (i, patch) => set({ folders: cfg.folders.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const togglePhase = (i, key) => { const f = cfg.folders[i]; setFolder(i, { phase_keys: f.phase_keys.includes(key) ? f.phase_keys.filter((k) => k !== key) : [...f.phase_keys, key] }); };

  const changeMode = async (mode) => {
    try {
      await api.put("/admin/backup/storage-mode", { mode });
      toast.success(`Storage mode: ${mode === "manual" ? "Manual Project Archive" : mode}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      await api.put("/admin/backup/settings", { provider: cfg.provider, destination_root: cfg.destination.root_folder, auto_enabled: cfg.auto_enabled, frequency_hours: cfg.frequency_hours, connection: cfg.connection });
      await api.put("/admin/backup", { enabled: cfg.enabled, connection: cfg.connection, root_pattern: cfg.root_pattern, folders: cfg.folders });
      toast.success("Backup & Storage settings saved");
      refresh();
      if (planFor) api.get(`/projects/${planFor}/backup-plan`).then((r) => setPlan(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runNow = async () => {
    setRunning(true);
    try { const { data } = await api.post("/admin/backup/run"); toast.success(`Backup ${data.status}: ${data.uploaded} uploaded, ${data.skipped} skipped, ${data.failed} failed`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); refresh(); }
  };

  if (!cfg) return <AdminPage title="Backup & Storage" testid="admin-backup-page"><p className="text-slate-500">Loading…</p></AdminPage>;

  const manual = (cfg.storage_mode || "manual") === "manual";
  return (
    <AdminPage title="Backup & Storage" subtitle="Store project evidence your way: download a structured ZIP per project, or automatically mirror to a cloud provider using the fixed 11-folder structure." testid="admin-backup-page"
      actions={<Button onClick={save} disabled={!dirty} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="backup-save-btn"><Save className="h-4 w-4 mr-2" />Save settings</Button>}>
      <StorageModeCard mode={cfg.storage_mode || "manual"} onChange={changeMode} />
      {!manual && (
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <ProviderCard cfg={cfg} providers={providers} set={set} setConn={setConn} onRefresh={refresh} />
          <ScheduleCard cfg={cfg} status={status} set={set} onRunNow={runNow} running={running} />
          <StatusCard status={status} />
        </div>
      )}
      <FolderMapping cfg={cfg} setFolder={setFolder} togglePhase={togglePhase} />
      <PlanPreview projects={projects} planFor={planFor} setPlanFor={setPlanFor} plan={plan} />
      <AuditLog rows={audit} />
    </AdminPage>
  );
}
